import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';

export default function FractionalMarketGrowth() {
  const data = [
    { year: '2024', value: 400, label: '$400M', isCurrent: true },
    { year: '2026', value: 1500, label: '$1.5B', estimated: true },
    { year: '2028', value: 3200, label: '$3.2B', estimated: true },
    { year: '2030', value: 5000, label: '$5B', estimated: true }
  ];

  const maxValue = 5200;

  return (
    <div className="py-32 bg-[#0A0A0A] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center space-x-2 px-6 py-3 bg-white/5 border border-white/10 rounded-full mb-8">
            <Sparkles className="w-4 h-4 text-[#4ADE80]" />
            <span className="text-sm text-white/80 tracking-wide">THE FRACTIONAL REVOLUTION</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-7xl font-light mb-6 leading-tight tracking-tight">
            <span className="block text-white/40 mb-2">FRACTIONAL OWNERSHIP</span>
            <span className="block">TAKING OVER.</span>
          </h2>
          <p className="text-xl text-white/50 max-w-3xl mx-auto leading-relaxed">
            the fractional real estate market in India is exploding from $400 million today 
            to <span className="text-[#4ADE80]">$5 billion by 2030</span>. a 12.5x surge in just 6 years.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Chart */}
          <div className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-2xl p-8">
            <div className="relative h-80">
              {/* Y-axis labels */}
              <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-xs text-white/40 pr-4">
                <span>$5B</span>
                <span>$4B</span>
                <span>$3B</span>
                <span>$2B</span>
                <span>$1B</span>
                <span>$0</span>
              </div>

              {/* Chart area */}
              <div className="ml-12 h-full relative">
                {/* Grid lines */}
                {[0, 20, 40, 60, 80, 100].map((y) => (
                  <div
                    key={y}
                    className="absolute w-full border-t border-white/5"
                    style={{ top: `${y}%` }}
                  />
                ))}

                {/* SVG for plotted line and points */}
                <svg className="absolute inset-0 w-full h-full overflow-visible" preserveAspectRatio="none">
                  {/* Plotted line segments */}
                  {data.map((point, index) => {
                    if (index === 0) return null;
                    const prevPoint = data[index - 1];
                    const x1 = ((index - 1) / (data.length - 1)) * 100;
                    const y1 = 100 - ((prevPoint.value / maxValue) * 100);
                    const x2 = (index / (data.length - 1)) * 100;
                    const y2 = 100 - ((point.value / maxValue) * 100);

                    return (
                      <motion.line
                        key={index}
                        x1={`${x1}%`}
                        y1={`${y1}%`}
                        x2={`${x2}%`}
                        y2={`${y2}%`}
                        stroke="#4ADE80"
                        strokeWidth="3"
                        strokeLinecap="round"
                        initial={{ pathLength: 0, opacity: 0 }}
                        whileInView={{ pathLength: 1, opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: index * 0.3 }}
                      />
                    );
                  })}

                  {/* Data points */}
                  {data.map((point, index) => {
                    const x = (index / (data.length - 1)) * 100;
                    const y = 100 - ((point.value / maxValue) * 100);

                    return (
                      <motion.g
                        key={index}
                        initial={{ scale: 0, opacity: 0 }}
                        whileInView={{ scale: 1, opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.3, delay: index * 0.3 + 0.2 }}
                      >
                        {/* Outer glow */}
                        <circle
                          cx={`${x}%`}
                          cy={`${y}%`}
                          r="12"
                          fill={point.isCurrent ? "rgba(74, 222, 128, 0.2)" : "rgba(255, 255, 255, 0.1)"}
                        />
                        {/* Outer circle */}
                        <circle
                          cx={`${x}%`}
                          cy={`${y}%`}
                          r="8"
                          fill={point.isCurrent ? "#4ADE80" : "#FFFFFF"}
                          stroke={point.isCurrent ? "#4ADE80" : "#FFFFFF"}
                          strokeWidth="2"
                        />
                        {/* Inner circle */}
                        <circle
                          cx={`${x}%`}
                          cy={`${y}%`}
                          r="4"
                          fill="#0A0A0A"
                        />
                      </motion.g>
                    );
                  })}
                </svg>

                {/* Labels overlay */}
                <div className="absolute inset-0 pointer-events-none">
                  {data.map((point, index) => {
                    const xPercent = (index / (data.length - 1)) * 100;
                    const yPercent = 100 - ((point.value / maxValue) * 100);

                    return (
                      <motion.div
                        key={index}
                        className="absolute"
                        style={{ left: `${xPercent}%`, top: `${yPercent}%` }}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.3 + 0.5 }}
                      >
                        <div className="transform -translate-x-1/2 -translate-y-full -mt-6">
                          <div className={`rounded-lg px-3 py-2 text-center shadow-lg ${
                            point.isCurrent
                              ? 'bg-[#4ADE80] text-black'
                              : 'bg-[#111111] border border-white/20'
                          }`}>
                            <p className="text-xs mb-0.5 opacity-80">{point.year}</p>
                            <p className="text-sm font-mono font-semibold">{point.label}</p>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* X-axis years */}
            <div className="flex justify-between ml-12 mt-4 text-xs text-white/40">
              {data.map((point, index) => (
                <div key={index} className={`text-center ${point.isCurrent ? 'text-[#4ADE80]' : ''}`}>
                  {point.year}
                </div>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-gradient-to-br from-[#4ADE80]/10 to-transparent border border-[#4ADE80]/20 rounded-2xl p-8"
            >
              <p className="text-6xl font-mono text-[#4ADE80] mb-4">12.5x</p>
              <p className="text-xl text-white mb-2">Market Multiplication</p>
              <p className="text-white/60 leading-relaxed">
                From $400M to $5B in just 6 years. The fractional ownership model is democratizing 
                real estate investment in India.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="bg-white/5 border border-white/10 rounded-2xl p-8"
            >
              <p className="text-6xl font-mono text-white mb-4">62%</p>
              <p className="text-xl text-white mb-2">Annual Growth Rate</p>
              <p className="text-white/60 leading-relaxed">
                Compound annual growth rate (CAGR) from 2024 to 2030. One of the fastest-growing 
                investment segments in India.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.7 }}
              className="bg-white/5 border border-white/10 rounded-2xl p-8"
            >
              <p className="text-6xl font-mono text-white mb-4">₹10L</p>
              <p className="text-xl text-white mb-2">Entry Point</p>
              <p className="text-white/60 leading-relaxed">
                Start your fractional ownership journey today. 
                Premium real estate made accessible.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}